import * as React from 'react';
import { CHANGE_KIND } from '@/lib/copy';
import { elidePath, formatDelta, shortSha } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CopyValue } from '@/components/ui/copy-value';
import { Flag } from '@/components/state-chip';
import { segmentLine, type Highlighter } from './use-highlighter';
import { ROW_HEIGHT, type DiffRow } from './rows';

/* ---------------------------------------------------------------------------
   The row renderers.

   One component per row kind, all of them dumb. Everything expensive — the flat
   row model, the offset table, the tokenising — happens outside; these functions
   only paint. That separation is what makes the virtualiser trustworthy: a row
   cannot change its own height, so the offset table computed once cannot go
   stale, and a `-` line can never end up drawn under the wrong file header.

   TWO SCROLL BEHAVIOURS, one pane. Code lines are `white-space: pre` and extend
   as far as they need to, so the pane scrolls horizontally for them. Structural
   rows — repo, file, hunk, note — are pinned to the left edge at the pane's own
   width, because a file path that scrolls away from a line it labels is worse
   than useless. The line-number gutters are pinned for the same reason: they are
   the instrument's ruler and they stay put.

   A LINE ROW IS THE HOT PATH. Tens of thousands exist and a few hundred are
   mounted at once, so it does the minimum. The add/delete tint is a background
   at low alpha, and the same fact is repeated as a `+` or `−` CHARACTER in the
   marker column — so the diff survives a colour-blind reviewer, a bad monitor,
   and a screenshot pasted into a ticket.
--------------------------------------------------------------------------- */

/** Width of the pane, for the rows that must not scroll out of it. */
function pinned(portWidth: number): React.CSSProperties {
  return portWidth > 0
    ? { position: 'sticky', left: 0, width: `${portWidth}px` }
    : { position: 'sticky', left: 0, width: '100%' };
}

export function DiffRowView({
  row,
  highlighter,
  portWidth,
  onOpenRepo,
}: {
  row: DiffRow;
  highlighter: Highlighter;
  portWidth: number;
  onOpenRepo: (repoId: number) => void;
}) {
  switch (row.kind) {
    case 'repo':
      return <RepoHeaderRow row={row} portWidth={portWidth} onOpenRepo={onOpenRepo} />;
    case 'file':
      return <FileHeaderRow row={row} portWidth={portWidth} />;
    case 'hunk':
      return <HunkHeaderRow row={row} portWidth={portWidth} />;
    case 'line':
      return <LineRow row={row} highlighter={highlighter} />;
    case 'note':
      return <NoteRow row={row} portWidth={portWidth} />;
    default: {
      const exhaustive: never = row;
      void exhaustive;
      return null;
    }
  }
}

function RepoHeaderRow({
  row,
  portWidth,
  onOpenRepo,
}: {
  row: Extract<DiffRow, { kind: 'repo' }>;
  portWidth: number;
  onOpenRepo: (repoId: number) => void;
}) {
  const repo = row.repo;
  return (
    <div
      className="z-20 flex items-center gap-2.5 border-y border-rule-strong bg-sunken px-3"
      style={{ ...pinned(portWidth), height: ROW_HEIGHT.repo, zIndex: 20 }}
    >
      <span className="shrink-0 font-mono text-xs font-semibold text-ink">{repo.repo_slug}</span>
      <span
        className="shrink-0 font-mono text-tiny text-ink-3"
        title="Promotion order — the order the merges will run in"
      >
        #{repo.position ?? 0}
      </span>
      <span className="truncate font-mono text-tiny text-ink-3">
        {repo.base_ref ?? 'target'} ← {repo.head_ref ?? 'pre-staging'}
      </span>
      {repo.head_sha ? (
        <CopyValue
          className="shrink-0 text-tiny"
          value={repo.head_sha}
          display={shortSha(repo.head_sha)}
          title={`Copy the head SHA of ${repo.repo_slug}`}
        />
      ) : null}
      <span className="ml-auto flex shrink-0 items-center gap-2.5">
        {repo.truncated ? (
          <button
            type="button"
            onClick={() => onOpenRepo(repo.repo_id)}
            className="font-mono text-tiny uppercase tracking-[0.08em] text-action underline underline-offset-2"
          >
            Open full diff
          </button>
        ) : null}
        {repo.changed ? (
          <span className="font-mono text-tiny tabular-nums text-ink-2">
            {formatDelta(repo.additions, repo.deletions)}
          </span>
        ) : (
          <span className="font-mono text-tiny text-ink-3">unchanged</span>
        )}
      </span>
    </div>
  );
}

function FileHeaderRow({ row, portWidth }: { row: Extract<DiffRow, { kind: 'file' }>; portWidth: number }) {
  const file = row.file;
  const kind = CHANGE_KIND[file.change_kind];
  return (
    <div
      className="flex items-center gap-2 border-b border-rule bg-raised px-3"
      style={{ ...pinned(portWidth), height: ROW_HEIGHT.file, zIndex: 15 }}
    >
      <span
        aria-hidden
        title={kind.label}
        className={cn(
          'w-[10px] shrink-0 text-center font-mono text-xs font-semibold',
          kind.tone === 'ok' && 'text-ok',
          kind.tone === 'danger' && 'text-danger',
          kind.tone === 'warn' && 'text-warn',
          kind.tone === 'neutral' && 'text-ink-3',
        )}
      >
        {kind.short}
      </span>
      <CopyValue
        className="min-w-0 text-xs text-ink"
        value={file.path}
        display={elidePath(file.path, 90)}
        title={`Copy ${file.path}`}
      />
      {file.old_path ? (
        <span className="shrink-0 font-mono text-tiny text-ink-3" title={`Was ${file.old_path}`}>
          ← {elidePath(file.old_path, 36)}
        </span>
      ) : null}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        {file.is_binary ? <Flag label="binary" tone="neutral" /> : null}
        {file.truncated ? (
          <Flag
            label="truncated"
            tone="warn"
            title="Over the per-file line budget. The counts are accurate; the hunks were cut."
          />
        ) : null}
        {file.language ? <span className="font-mono text-tiny text-ink-3">{file.language}</span> : null}
        <span className="font-mono text-tiny tabular-nums text-ink-2">
          {formatDelta(file.additions, file.deletions)}
        </span>
      </span>
    </div>
  );
}

function HunkHeaderRow({ row, portWidth }: { row: Extract<DiffRow, { kind: 'hunk' }>; portWidth: number }) {
  return (
    <div
      className="flex items-center gap-3 border-y border-rule bg-ground px-3"
      style={{ ...pinned(portWidth), height: ROW_HEIGHT.hunk, zIndex: 10 }}
    >
      <span className="shrink-0 font-mono text-tiny tabular-nums text-ink-3">
        @@ −{row.oldStart} +{row.newStart}
      </span>
      {row.header ? (
        <span className="truncate font-mono text-tiny text-ink-2" title={row.header}>
          {row.header}
        </span>
      ) : null}
    </div>
  );
}

function NoteRow({ row, portWidth }: { row: Extract<DiffRow, { kind: 'note' }>; portWidth: number }) {
  return (
    <div
      className={cn(
        'flex items-center border-b border-rule px-3 text-xs',
        row.tone === 'warn' ? 'bg-warn-soft text-ink' : 'bg-surface text-ink-2',
      )}
      style={{ ...pinned(portWidth), height: ROW_HEIGHT.note, zIndex: 10 }}
    >
      <p className="truncate">{row.text}</p>
    </div>
  );
}

const GUTTER =
  'sticky z-[5] w-[52px] shrink-0 select-none border-r border-rule bg-surface px-1 text-right font-mono text-tiny leading-[18px] tabular-nums text-ink-3';

/**
 * One diff line.
 *
 * Memoised: a scroll moves the window, and a row that stayed inside it must not
 * re-render because a sibling arrived. `highlighter.get` is read on every render
 * rather than subscribed to, and the hook bumps a version when a batch lands —
 * which is what makes a late tokenising result appear without the row needing to
 * hold any state of its own.
 */
const LineRow = React.memo(function LineRow({
  row,
  highlighter,
}: {
  row: Extract<DiffRow, { kind: 'line' }>;
  highlighter: Highlighter;
}) {
  const { line } = row;
  const segments = segmentLine(line.content, highlighter.get(row.key));
  const marker = line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : ' ';

  return (
    <div className="flex w-max min-w-full items-stretch" style={{ height: ROW_HEIGHT.line }} data-kind={line.kind}>
      <span className={GUTTER} style={{ left: 0 }} aria-hidden>
        {line.old_line ?? ''}
      </span>
      <span className={GUTTER} style={{ left: 52 }} aria-hidden>
        {line.new_line ?? ''}
      </span>
      <span
        className={cn(
          'flex flex-1 items-stretch',
          line.kind === 'add' && 'bg-diff-add',
          line.kind === 'del' && 'bg-diff-del',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'w-[15px] shrink-0 select-none text-center font-mono text-tiny leading-[18px]',
            line.kind === 'add' && 'text-diff-add-ink',
            line.kind === 'del' && 'text-diff-del-ink',
            line.kind === 'context' && 'text-ink-3',
          )}
        >
          {marker}
        </span>
        <code className="diff-line flex-1 pr-6 text-ink">
          {segments.map((segment, index) =>
            segment.cls ? (
              <span key={index} className={segment.cls}>
                {segment.text}
              </span>
            ) : (
              <React.Fragment key={index}>{segment.text}</React.Fragment>
            ),
          )}
        </code>
      </span>
    </div>
  );
});
